import dayjs from 'dayjs';
import { parseWallClockTime } from '../../../shared/Helpers/timezones';

import { RecurringScheduleSessionProperties } from '../../../redux/API/ClientAPIHelpers/calendarProperties';
import {
	findMatchingRealSessionIndex,
	isDateWithinCancellationRange,
	shouldCreateRecurringScheduleSessionForDate,
	timeToMinutes,
} from '../../../redux/State/clientSlice/calendarSlice';
import { BtClient, BtDashboardSession } from '../../../redux/State/clientSlice/dashboardSlice';
import { InfoAvailableClientsProperties } from '../../../redux/API/ClientAPIHelpers/userClientProperties';
import { CancellationStatus, ScheduleCancellationViewModel } from '../../../redux/API/ClientAPIHelpers/scheduleCancellation';

const minutesToIso = (date: dayjs.Dayjs, minutes: number) =>
	`${date.format('YYYY-MM-DD')}T${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}:00`;

const realSessionKey = (clientId: string, sessionTypeId: number) => `${clientId}|${sessionTypeId}`;

const isEffectivelyCancelled = (schedule: RecurringScheduleSessionProperties, date: dayjs.Dayjs, cancellations: ScheduleCancellationViewModel[]) =>
	cancellations.some((cancellation) =>
		(cancellation.status === CancellationStatus.Applied || cancellation.status === CancellationStatus.Approved) &&
		cancellation.providerId === schedule.providerId &&
		(!cancellation.clientId || cancellation.clientId === schedule.clientId) &&
		(!cancellation.recurringScheduleId || cancellation.recurringScheduleId === schedule.id) &&
		(!cancellation.recurringScheduleTimeSlotId || cancellation.recurringScheduleTimeSlotId === schedule.recurringScheduleTimeSlotId) &&
		isDateWithinCancellationRange(date.toDate(), cancellation.startDate, cancellation.endDate));

const buildGhostClient = (
	schedule: RecurringScheduleSessionProperties,
	client?: InfoAvailableClientsProperties,
): InfoAvailableClientsProperties =>
	client ?? {
		id: schedule.clientId,
		fullName: schedule.clientName,
		firstName: '',
		lastName: '',
		email: '',
		gender: '',
		dateOfBirthday: '',
		isElc: schedule.isElc ?? false,
		isAssigned: true,
	};

// Reuses the calendar's expansion + de-dup so today's scheduled occurrences appear on the BT
// dashboard exactly as they do on the calendar: a real session occurring today (overlapping window
// or within tolerance) suppresses its matching ghost; each real session consumes at most one ghost.
export const buildTodayGhostSessions = (
	recurringSchedules: RecurringScheduleSessionProperties[],
	realSessions: BtDashboardSession[],
	clients: BtClient[],
	today: dayjs.Dayjs = dayjs(),
	cancellations: ScheduleCancellationViewModel[] = [],
): BtDashboardSession[] => {
	const clientsById = new Map(clients.map((c) => [c.client.id, c]));

	const realByKey = new Map<string, { startMinutes: number; endMinutes: number; recurringScheduleTimeSlotId?: string | null; used: boolean }[]>();
	realSessions.forEach((session) => {
		const key = realSessionKey(session.client.id, session.sessionTypeId);
		const windows = realByKey.get(key) ?? [];
		windows.push({ startMinutes: timeToMinutes(session.startTime), endMinutes: timeToMinutes(session.endTime), recurringScheduleTimeSlotId: session.recurringScheduleTimeSlotId, used: false });
		realByKey.set(key, windows);
	});

	const ghosts: BtDashboardSession[] = [];

	recurringSchedules.forEach((schedule) => {
		if (!shouldCreateRecurringScheduleSessionForDate(schedule, today)) return;
		if (isEffectivelyCancelled(schedule, today, cancellations)) return;

		const key = realSessionKey(schedule.clientId, schedule.sessionTypeId);
		const candidates = realByKey.get(key);

		if (candidates) {
			const matchIndex = findMatchingRealSessionIndex(candidates, schedule.startTimeMinutes, schedule.endTimeMinutes, schedule.recurringScheduleTimeSlotId);
			if (matchIndex !== -1) {
				candidates[matchIndex].used = true;
				return;
			}
		}

		const btClient = clientsById.get(schedule.clientId);

		ghosts.push({
			sessionId: null,
			eventId: '',
			client: buildGhostClient(schedule, btClient?.client),
			startTime: minutesToIso(today, schedule.startTimeMinutes),
			endTime: minutesToIso(today, schedule.endTimeMinutes),
			locationName: null,
			locationAddress: null,
			isTelehealth: false,
			activeTargetsCount: btClient?.activeTargetsCount ?? 0,
			isCompleted: false,
			sessionTypeId: schedule.sessionTypeId,
			sessionTypeType: schedule.sessionType,
			isRecurringScheduleOccurrence: true,
			recurringScheduleId: schedule.id,
			recurringScheduleTimeSlotId: schedule.recurringScheduleTimeSlotId,
		});
	});

	return ghosts;
};

export const mergeTodaySessions = (
	realSessions: BtDashboardSession[],
	ghostSessions: BtDashboardSession[],
): BtDashboardSession[] =>
	[...realSessions, ...ghostSessions].sort((a, b) => parseWallClockTime(a.startTime).valueOf() - parseWallClockTime(b.startTime).valueOf());

// Client-card schedule is the BT's own recurring days for each client (the rows are already scoped
// to the current provider server-side). scheduleDays = distinct weekdays; sessionsPerWeek = total
// scheduled occurrences across a representative week.
export const deriveClientScheduleInfo = (
	recurringSchedules: RecurringScheduleSessionProperties[],
): Record<string, { scheduleDays: number[]; sessionsPerWeek: number }> => {
	const byClient: Record<string, { days: Set<number>; sessionsPerWeek: number }> = {};

	recurringSchedules.forEach((schedule) => {
		const entry = byClient[schedule.clientId] ?? { days: new Set<number>(), sessionsPerWeek: 0 };
		schedule.daysOfWeek.forEach((day) => entry.days.add(day));
		entry.sessionsPerWeek += schedule.daysOfWeek.length;
		byClient[schedule.clientId] = entry;
	});

	return Object.fromEntries(
		Object.entries(byClient).map(([clientId, { days, sessionsPerWeek }]) => [
			clientId,
			{ scheduleDays: Array.from(days).sort((a, b) => a - b), sessionsPerWeek },
		]),
	);
};
